import type { InvestorDocument } from '@/types/investor';

export const DOC_VISIBILITY_ROLES = [
  { value: 'closer', label: 'Closer' },
  { value: 'compliance_specialist', label: 'Compliance Specialist' },
  { value: 'loan_officer', label: 'Loan Officer' },
  { value: 'loan_officer_assistant', label: 'Loan Officer Assistant' },
  { value: 'manager', label: 'Manager' },
  { value: 'processor', label: 'Processor' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'underwriter', label: 'Underwriter' }
] as const;

export const DOC_VISIBILITY_ROLE_VALUES = DOC_VISIBILITY_ROLES.map((role) => role.value);

/** Roles that can always see any investor document, regardless of hidden_roles. */
export const ALWAYS_ALLOWED_DOC_ROLES = ['admin', 'partner'] as const;

export function hiddenRolesFromAllowedRoles(allowedRoles?: readonly string[] | null): string[] {
  if (!allowedRoles || allowedRoles.length === 0) {
    return [];
  }
  const allowed = new Set(allowedRoles.map((role) => role.toLowerCase()));
  return DOC_VISIBILITY_ROLE_VALUES.filter((role) => !allowed.has(role));
}

/**
 * UI-level document visibility (Manage Investors v2).
 *
 * NOTE: this is display filtering, NOT enforced access control — the `documents`
 * bucket is public. It governs only what the UI surfaces:
 *  - empty / undefined `hidden_roles` → visible to all
 *  - a user holding `admin` or `partner` → always visible
 *  - otherwise → visible if at least one user role is not hidden
 *
 * Role comparison is case-insensitive.
 */
export function isDocumentVisibleForRoles(
  doc: Pick<InvestorDocument, 'hiddenRoles' | 'allowedRoles'>,
  userRoles: readonly string[]
): boolean {
  const hidden = doc.hiddenRoles ?? hiddenRolesFromAllowedRoles(doc.allowedRoles);
  if (hidden.length === 0) {
    return true;
  }
  const roles = userRoles.map((r) => r.toLowerCase());
  if (ALWAYS_ALLOWED_DOC_ROLES.some((r) => roles.includes(r))) {
    return true;
  }
  if (roles.length === 0) {
    return false;
  }
  const hiddenSet = new Set(hidden.map((role) => role.toLowerCase()));
  return roles.some((role) => !hiddenSet.has(role));
}
